import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getLanguageFromPath, switchLanguagePath } from '../lib/languagePaths';

// 双语站标准模式：当访问者落在与其浏览器偏好不符的页面（例如 Google 把 /zh/
// URL 提供给英文用户）时，显示一条可关闭的建议横幅，链接到同页面的另一种语言。
// 绝不自动跳转（Google 规范；爬虫与有意访问者不应被强制）。
//
// 仅当以下条件全部满足时显示：
//  - 浏览器语言与页面语言不一致
//  - 用户从未明确选择过语言（i18n 在手动切换时写 localStorage.language，明确选择优先）
//  - 横幅此前未被关闭
// 在 useEffect 中判定，避免首屏渲染闪烁（纯 CSR，无预渲染）。

const DISMISS_KEY = 'langSuggestionDismissed';
const LANGUAGE_KEY = 'language';

// 文案写在「建议的语言」里（访问者自己的语言）。
const COPY = {
  en: { message: 'This page is also available in English.', action: 'View in English', dismiss: 'Dismiss' },
  zh: { message: '此页面提供中文版。', action: '查看中文版', dismiss: '关闭' },
};

// 只看主浏览器语言：一个 [en-GB, en-AU, zh-Hans-AU] 的用户以英文为主——
// 在英文页面向其建议中文只会是噪音。
const browserPrefersChinese = () => {
  const langs =
    (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
  return String(langs[0] || '').toLowerCase().startsWith('zh');
};

const LanguageSuggestionBanner = () => {
  // App 在中文页用 basename="/zh" 挂载 Router，因此 useLocation().pathname 已剥离
  // 语言前缀。语言判断与链接构建须用 window.location.pathname（与 LanguageToggle
  // 相同的坑）；useLocation 只用于在 SPA 导航时重跑 effect。
  const location = useLocation();
  const [suggest, setSuggest] = useState(null); // 'en' | 'zh' | null

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return setSuggest(null);
      if (localStorage.getItem(LANGUAGE_KEY)) return setSuggest(null); // 明确选择优先
    } catch (_) { /* 存储不可用——继续建议 */ }

    const pageLang = getLanguageFromPath(window.location.pathname);
    const wantsZh = browserPrefersChinese();
    if (pageLang === 'zh' && !wantsZh) setSuggest('en');
    else if (pageLang === 'en' && wantsZh) setSuggest('zh');
    else setSuggest(null);
  }, [location.pathname]);

  if (!suggest) return null;

  const copy = COPY[suggest];
  const targetHref = switchLanguagePath(window.location.pathname, location.search, location.hash, suggest);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* ignore */ }
    setSuggest(null);
  };

  const switchNow = (event) => {
    event.preventDefault();
    // 视为一次明确的语言选择，此后不再提示。
    try { localStorage.setItem(LANGUAGE_KEY, suggest); } catch (_) { /* ignore */ }
    window.location.href = targetHref;
  };

  return (
    <div className="cy-lang-suggest" role="status">
      <span>{copy.message}</span>
      <a className="cy-lang-suggest__action" href={targetHref} onClick={switchNow}>
        {copy.action} →
      </a>
      <button type="button" className="cy-lang-suggest__close" aria-label={copy.dismiss} onClick={dismiss}>
        ✕
      </button>
    </div>
  );
};

export default LanguageSuggestionBanner;
